import { hasTrackingConsent } from "./consent";
import {
  getStoredContext,
  isContextCode,
  safeSessionGet,
  safeSessionSet,
} from "./session";

/**
 * First-touch attribution of a website visit, computed on the first page of the visit.
 * Empty strings mean "not present" (they are dropped before sending).
 */
export type Attribution = {
  /** `utm_source`, else the classified referrer (`google`, `linkedin`, host…), else `direct`. */
  source: string;
  /** `utm_medium`, else `organic` / `social` / `referral`, else `none`. */
  medium: string;
  campaign: string;
  content: string;
  term: string;
  /** Host of an external referrer; never the website's own host. */
  referrerHost: string;
  /** Path of the first page of the visit, without query string or hash. */
  landingPath: string;
  /** 6-character context code of the visit (flyer, business card, partner link). */
  context: string;
};

const STORAGE_KEY = "flots-obs-attribution";
const MAX_VALUE_LENGTH = 100;
const OWN_HOSTS = new Set(["flots.app", "www.flots.app", "localhost", "127.0.0.1"]);

/** Referrer hosts we name instead of reporting the raw host, with their medium. */
const KNOWN_REFERRERS: { re: RegExp; source: string; medium: "organic" | "social" }[] = [
  { re: /(^|\.)google\.[a-z.]+$|^com\.google\.android\.googlequicksearchbox$/, source: "google", medium: "organic" },
  { re: /(^|\.)bing\.com$/, source: "bing", medium: "organic" },
  { re: /(^|\.)duckduckgo\.com$/, source: "duckduckgo", medium: "organic" },
  { re: /(^|\.)qwant\.com$/, source: "qwant", medium: "organic" },
  { re: /(^|\.)ecosia\.org$/, source: "ecosia", medium: "organic" },
  { re: /(^|\.)yahoo\.[a-z.]+$/, source: "yahoo", medium: "organic" },
  { re: /(^|\.)linkedin\.com$|^lnkd\.in$/, source: "linkedin", medium: "social" },
  { re: /(^|\.)(facebook\.com|fb\.com|fb\.me)$/, source: "facebook", medium: "social" },
  { re: /(^|\.)instagram\.com$/, source: "instagram", medium: "social" },
  { re: /(^|\.)(x\.com|twitter\.com|t\.co)$/, source: "x", medium: "social" },
  { re: /(^|\.)(youtube\.com|youtu\.be)$/, source: "youtube", medium: "social" },
  { re: /(^|\.)tiktok\.com$/, source: "tiktok", medium: "social" },
  { re: /(^|\.)reddit\.com$/, source: "reddit", medium: "social" },
  { re: /(^|\.)github\.com$/, source: "github", medium: "social" },
];

// Module-level on purpose: survives client-side navigation without touching storage, so the
// first page of the visit is remembered even when the visitor never accepts cookies.
let current: Attribution | null = null;

function clean(value: string | null | undefined): string {
  return (value ?? "").trim().slice(0, MAX_VALUE_LENGTH);
}

/**
 * Host of `document.referrer`, or "" when there is none or it is the website itself.
 *
 * @returns Lower-cased host without a leading `www.`.
 */
export function externalReferrerHost(): string {
  if (typeof document === "undefined" || !document.referrer) return "";
  let host: string;
  try {
    host = new URL(document.referrer).hostname.toLowerCase();
  } catch {
    return "";
  }
  if (!host || OWN_HOSTS.has(host) || host === window.location.hostname.toLowerCase()) return "";
  return host.replace(/^www\./, "");
}

/**
 * Names a referrer host.
 *
 * @param host - External referrer host ("" for none).
 * @returns The source (`google`, `linkedin`, the host itself, or `direct`) and its medium.
 */
export function classifyReferrer(host: string): { source: string; medium: string } {
  if (!host) return { source: "direct", medium: "none" };
  const known = KNOWN_REFERRERS.find((r) => r.re.test(host));
  if (known) return { source: known.source, medium: known.medium };
  return { source: host, medium: "referral" };
}

function compute(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const referrerHost = clean(externalReferrerHost());
  const classified = classifyReferrer(referrerHost);
  const utmSource = clean(params.get("utm_source"));
  const utmMedium = clean(params.get("utm_medium"));
  const pathname = window.location.pathname || "/";
  const pathCode = pathname.replace(/^\//, "");

  return {
    source: utmSource || classified.source,
    medium: utmMedium || classified.medium,
    campaign: clean(params.get("utm_campaign")),
    content: clean(params.get("utm_content")),
    term: clean(params.get("utm_term")),
    referrerHost,
    landingPath: clean(pathname),
    context: getStoredContext() ?? (isContextCode(pathCode) ? pathCode : ""),
  };
}

function restore(): Attribution | null {
  if (!hasTrackingConsent()) return null;
  const raw = safeSessionGet(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    if (typeof parsed.source !== "string" || typeof parsed.landingPath !== "string") return null;
    return {
      source: clean(parsed.source),
      medium: clean(parsed.medium),
      campaign: clean(parsed.campaign),
      content: clean(parsed.content),
      term: clean(parsed.term),
      referrerHost: clean(parsed.referrerHost),
      landingPath: clean(parsed.landingPath),
      context: clean(parsed.context),
    };
  } catch {
    return null;
  }
}

/**
 * Returns the first-touch attribution of this visit. The first call (made by the
 * observability provider on the first page) computes it from the URL and the referrer; later
 * calls return the same value. After a reload, a copy saved with consent is reused so the
 * reload does not look like a new direct visit.
 *
 * @returns The attribution, or null on the server.
 */
export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  if (!current) current = restore() ?? compute();
  // The context code can be stored by the redirect page after the first computation.
  if (!current.context) {
    const stored = getStoredContext();
    if (stored) current = { ...current, context: stored };
  }
  return current;
}

/**
 * Saves the attribution in sessionStorage so a reload of the tab keeps it. Only runs once the
 * visitor accepted cookies; without consent the attribution lives in memory only.
 */
export function persistAttribution(): void {
  if (!hasTrackingConsent()) return;
  const attribution = getAttribution();
  if (attribution) safeSessionSet(STORAGE_KEY, JSON.stringify(attribution));
}
